import * as React from "react"
import Link from "next/link"
import type { LinkProps } from "next/link"

import { classifyAnchorHref } from "./anchor-classification"

// Combine LinkProps with AnchorProps to support all Next.js Link props
type AnchorProps = Omit<React.ComponentPropsWithoutRef<"a">, "href"> &
  LinkProps & {
    href: string
    /** Defaults to true for third-party web links, false for everything else. */
    newTab?: boolean
    dofollow?: boolean
    sponsored?: boolean
  }

/**
 * Enhanced Anchor component that handles both internal and external links.
 *
 * Features:
 * - Uses Next.js Link for internal navigation
 * - Handles external links with appropriate rel attributes
 * - Supports all Next.js Link props
 * - Opens third-party web links in a new tab unless newTab={false}; internal,
 *   CitizenShipper (including app./help.) and mailto:/tel: links stay in the
 *   same tab unless newTab or target="_blank" is passed
 * - Special handling for CitizenShipper links to preserve analytics tracking and SEO
 */
export const Anchor = React.memo(
  ({
    href,
    children,
    newTab,
    className,
    dofollow = false,
    sponsored = false,
    prefetch = false, // Default to false to avoid unnecessary prefetching
    replace,
    scroll,
    shallow,
    locale,
    passHref,
    ...props
  }: AnchorProps) => {
    // Extract rel and target from props if provided. A caller-supplied
    // target="_blank" or "_self" wins over newTab and the default; left in
    // restProps it would override the computed target and skip the security
    // rel tokens.
    const userProvidedRel = props.rel
    const { rel: _, target: requestedTarget, ...restProps } = props

    // Validate and normalize the href
    const normalizedHref = React.useMemo(() => {
      try {
        // Handle empty or invalid hrefs
        if (!href || typeof href !== "string") {
          console.warn(`Invalid href provided to Anchor component: ${href}`, {
            href,
            children,
            component: "Anchor",
            stack: new Error().stack,
          })

          return "#"
        }

        return href
      } catch (error) {
        console.warn(`Error processing href in Anchor component: ${error}`, {
          href,
          children,
          component: "Anchor",
          error,
          stack: new Error().stack,
        })

        return "#"
      }
    }, [href, children])

    // Get URL information
    const urlInfo = React.useMemo(() => classifyAnchorHref(normalizedHref), [
      normalizedHref,
    ])

    // Third-party web links default to a new tab. CitizenShipper links (the
    // app.citizenshipper.com quote CTAs among them) and mailto:/tel: stay in
    // the same tab so conversion flows and empty tabs are not split off.
    const requestsSameTab = requestedTarget === "_self"
    const opensNewTab =
      requestedTarget === "_blank" ||
      (!requestsSameTab && (newTab ?? urlInfo.isThirdPartyWebLink))
    const target = opensNewTab ? "_blank" : "_self"

    // Build rel attribute
    const computedRel = React.useMemo(() => {
      const relParts = new Set<string>()

      // User-provided rel values take precedence
      if (userProvidedRel) {
        userProvidedRel.split(" ").forEach((part) => {
          if (part.trim()) {
            relParts.add(part.trim())
          }
        })
      }

      // Handle external links
      if (urlInfo.isExternal) {
        // Skip mailto: and tel: links for external attribution
        if (!urlInfo.isMailtoOrTel) {
          // Non-CitizenShipper external links get "external" rel
          if (!urlInfo.isCitizenShipper) {
            relParts.add("external")
          }
        }

        // SEO attributes based on props
        // Never add nofollow to trusted domains
        if (!dofollow && !urlInfo.isTrustedDomain) {
          relParts.add("nofollow")
        }

        if (sponsored) {
          relParts.add("sponsored")
        }
      }

      // Security attributes for new tab links
      if (target === "_blank") {
        relParts.add("noopener")

        // Add noreferrer for non-CitizenShipper links
        if (!urlInfo.isCitizenShipper || !urlInfo.isExternal) {
          relParts.add("noreferrer")
        }
      }

      // Special case for BBB links
      if (normalizedHref.includes("bbb.org") && target === "_blank") {
        relParts.add("external")
        relParts.add("noopener")
        relParts.add("noreferrer")
      }

      return Array.from(relParts).join(" ") || undefined
    }, [
      urlInfo.isExternal,
      urlInfo.isCitizenShipper,
      urlInfo.isMailtoOrTel,
      urlInfo.isTrustedDomain,
      normalizedHref,
      userProvidedRel,
      dofollow,
      sponsored,
      target,
    ])

    // Prepare props based on whether a class was provided
    const style = className
      ? undefined
      : {
          color: "#1d4ed8",
          backgroundColor: "transparent",
        }

    // Common props for both components. `data-anchor` lets the Cypress
    // link-rules spec (cypress/e2e/anchor.cy.ts) tell Anchor output apart from
    // raw <a> tags, which follow their own rel/target contracts.
    const commonProps = {
      "data-anchor": "",
      className: className
        ? `${className} hover:!underline`
        : "hover:!underline",
      style,
      rel: computedRel,
      ...restProps,
    }

    // Decide which component to render
    if (urlInfo.isExternal || target === "_blank") {
      // External links or any link opening in a new tab use a regular anchor tag
      return (
        <a href={normalizedHref} target={target} {...commonProps}>
          {children}
        </a>
      )
    } else {
      // Internal links opening in the same tab use Next.js Link
      return (
        <Link
          href={normalizedHref}
          prefetch={prefetch}
          replace={replace}
          scroll={scroll}
          shallow={shallow}
          locale={locale}
          passHref={passHref}
          {...commonProps}
        >
          {children}
        </Link>
      )
    }
  },
)

// Add display name for React DevTools and error messages
Anchor.displayName = "Anchor"
